import "./trustedTypes";
import { Buffer } from "buffer";
if (typeof globalThis.Buffer === "undefined") {
  globalThis.Buffer = Buffer;
}
import { createRoot } from "react-dom/client";
import "./landing.css";
import { initTelegramAnalytics } from "./lib/telegramAnalytics";
import { initErudaDebug } from "./lib/eruda";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import {
  clearStartupDiagnostic,
  formatStartupDiagnosticForDisplay,
  readStartupDiagnostic,
  recordFirstStartupDiagnostic,
} from "./lib/startupDiagnostics";
import { resetRecipmonServiceWorkerAndCaches } from "./lib/serviceWorkerRecovery";
import { STARTUP_MOUNT_EVENT, startStartupWatchdog } from "./lib/startupWatchdog";
import {
  safeSessionStorageGet,
  safeSessionStorageRemove,
  safeSessionStorageSet,
} from "./lib/safeStorage";
import {
  attachBootShellLifecycle,
  buildTelegramMiniAppRedirectUrl,
  hideBootShell,
  shouldRedirectTelegramRootToMiniApp,
  TELEGRAM_MINI_APP_PATH,
} from "./lib/bootShell";

const CHUNK_RELOAD_KEY = "recipmon:chunk-reload-attempted";
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module/i;
const PWA_ENABLED =
  String(import.meta.env.VITE_PWA_ENABLED ?? "true")
    .trim()
    .toLowerCase() === "true";
const PWA_DISABLE_IN_TELEGRAM =
  String(import.meta.env.VITE_PWA_DISABLE_IN_TELEGRAM ?? "true")
    .trim()
    .toLowerCase() === "true";
const TELEGRAM_SW_AUTO_RESET_ATTEMPTED_KEY = "recipmon:telegram-sw-auto-reset-attempted";
const TELEGRAM_SW_AUTO_RESET_QUERY_PARAM = "tg_sw_reset";
const BOOT_SHELL_MAX_VISIBLE_MS = 9000;
const IS_DEV_RUNTIME = Boolean(import.meta.env.DEV);

let hasStartupMounted = false;

if (
  window.location.pathname === "/" &&
  "performance" in window &&
  typeof window.performance.mark === "function"
) {
  window.performance.mark("landing-bootstrap-start");
}

const isLikelyTelegramHost = () => {
  const telegramGlobal = (window as typeof window & {
    Telegram?: { WebApp?: { platform?: string } };
  }).Telegram;
  
  if (telegramGlobal?.WebApp && telegramGlobal.WebApp.platform && telegramGlobal.WebApp.platform !== "unknown") {
    return true;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  
  const hasTgParam = (params: URLSearchParams) => (
    params.has("tgWebAppData") ||
    params.has("tgWebAppVersion") ||
    params.has("tgWebAppPlatform") ||
    params.has("tgWebAppThemeParams") ||
    params.has("tgWebAppStartParam")
  );

  return hasTgParam(searchParams) || hasTgParam(hashParams);
};

const isTelegramMiniAppRoute = () => window.location.pathname.startsWith("/recipmontelegram");

const shouldApplyTelegramMiniAppSwPolicy = () => isLikelyTelegramHost() || isTelegramMiniAppRoute();
const shouldDisableServiceWorkerForMiniAppContext = () =>
  isTelegramMiniAppRoute() || (PWA_DISABLE_IN_TELEGRAM && isLikelyTelegramHost());

const shouldInitTelegramAnalytics = () => {
  const isLandingRoute = window.location.pathname === "/";
  return !isLandingRoute || isLikelyTelegramHost();
};

const tryAutoRecoverChunkError = (reason: unknown) => {
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
      ? reason.message
      : String(reason ?? "");

  if (!CHUNK_ERROR_PATTERN.test(message)) {
    return;
  }

  if (safeSessionStorageGet(CHUNK_RELOAD_KEY) === "true") {
    return;
  }

  safeSessionStorageSet(CHUNK_RELOAD_KEY, "true");
  window.location.reload();
};

const scheduleAnalyticsInit = () => {
  if (!shouldInitTelegramAnalytics()) {
    return;
  }

  if ("requestIdleCallback" in window) {
    (window as Window).requestIdleCallback(() => {
      void initTelegramAnalytics();
    });
    return;
  }

  globalThis.setTimeout(() => {
    void initTelegramAnalytics();
  }, 800);
};

const registerServiceWorker = () => {
  if (IS_DEV_RUNTIME) {
    return;
  }
  if (!PWA_ENABLED || !("serviceWorker" in navigator)) {
    return;
  }
  if (shouldDisableServiceWorkerForMiniAppContext()) {
    return;
  }

  navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .then((registration) => {
      void registration.update().catch(() => undefined);
      return registration;
    })
    .catch(() => undefined);
};

const maybeAutoResetStaleMiniAppServiceWorker = async () => {
  if (!shouldDisableServiceWorkerForMiniAppContext()) {
    return false;
  }
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get(TELEGRAM_SW_AUTO_RESET_QUERY_PARAM) === "1") {
    return false;
  }

  if (safeSessionStorageGet(TELEGRAM_SW_AUTO_RESET_ATTEMPTED_KEY) === "true") {
    return false;
  }

  safeSessionStorageSet(TELEGRAM_SW_AUTO_RESET_ATTEMPTED_KEY, "true");
  const result = await resetRecipmonServiceWorkerAndCaches({
    reloadParam: TELEGRAM_SW_AUTO_RESET_QUERY_PARAM,
  }).catch((error) => {
    console.warn("[startup-recovery] Auto reset of Telegram Mini App service worker failed:", error);
    return null;
  });

  return Boolean(result?.reloadTriggered);
};

const onLoad = () => {
  void initErudaDebug();
  scheduleAnalyticsInit();
  globalThis.setTimeout(() => {
    safeSessionStorageRemove(CHUNK_RELOAD_KEY);
  }, 10000);

  // Prevent persistent white/blank startup when mount signal is missed.
  globalThis.setTimeout(() => {
    if (!hasStartupMounted) {
      hideBootShell();
    }
  }, BOOT_SHELL_MAX_VISIBLE_MS);

  if (currentUrlHasAutoResetMarker()) {
    globalThis.setTimeout(() => {
      safeSessionStorageRemove(TELEGRAM_SW_AUTO_RESET_ATTEMPTED_KEY);
    }, 2000);
  }

  const scheduleSwRegistration = () => {
    if ("requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(() => {
        registerServiceWorker();
      });
      return;
    }

    globalThis.setTimeout(() => {
      registerServiceWorker();
    }, 1200);
  };

  if (IS_DEV_RUNTIME) {
    void resetRecipmonServiceWorkerAndCaches({
      skipReload: true,
      reloadIfNoChanges: false,
    });
  }

  void maybeAutoResetStaleMiniAppServiceWorker().then((reloaded) => {
    if (!reloaded) {
      scheduleSwRegistration();
    }
  });
};

const currentUrlHasAutoResetMarker = () => {
  try {
    return new URL(window.location.href).searchParams.get(TELEGRAM_SW_AUTO_RESET_QUERY_PARAM) === "1";
  } catch {
    return false;
  }
};

const bootShellLifecycle = attachBootShellLifecycle();

const hasRedirectedTelegramRootToMiniApp = (() => {
  if (
    !shouldRedirectTelegramRootToMiniApp({
      pathname: window.location.pathname,
      isTelegramContext: isLikelyTelegramHost(),
    })
  ) {
    return false;
  }

  try {
    const targetUrl = buildTelegramMiniAppRedirectUrl({
      href: window.location.href,
      miniAppPath: TELEGRAM_MINI_APP_PATH,
    });
    window.location.replace(targetUrl);
    return true;
  } catch (error) {
    console.warn("[startup] Failed to build Telegram mini app redirect URL:", error);
    window.location.replace(TELEGRAM_MINI_APP_PATH);
    return true;
  }
})();

if (!hasRedirectedTelegramRootToMiniApp) {
  if (document.readyState === "complete") {
    onLoad();
  } else {
    window.addEventListener("load", onLoad, { once: true });
  }

  window.addEventListener("unhandledrejection", (event) => {
    if (!hasStartupMounted) {
      recordFirstStartupDiagnostic(event.reason);
    }
    tryAutoRecoverChunkError(event.reason);
  });

  window.addEventListener("error", (event) => {
    const reason = (event as ErrorEvent).error ?? event.message;
    if (!hasStartupMounted) {
      recordFirstStartupDiagnostic(reason);
    }
    tryAutoRecoverChunkError(reason);
  });

  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error('Root element "#root" not found.');
  }

  window.addEventListener(
    STARTUP_MOUNT_EVENT,
    () => {
      hasStartupMounted = true;
      const startupDiagnostic = readStartupDiagnostic();
      if (import.meta.env.DEV && startupDiagnostic) {
        console.info("[startup] Recovered from startup diagnostic:", startupDiagnostic.code);
      }
      clearStartupDiagnostic();
      bootShellLifecycle.remove();
    },
    { once: true },
  );

  startStartupWatchdog({
    pathname: window.location.pathname,
    isTelegramContext: shouldApplyTelegramMiniAppSwPolicy(),
    getStartupDiagnostic: () => readStartupDiagnostic(),
    formatDiagnostic: formatStartupDiagnosticForDisplay,
    onResetAppCache: () => resetRecipmonServiceWorkerAndCaches(),
    openMiniAppPath: TELEGRAM_MINI_APP_PATH,
  });

  const mountReactTree = async () => {
    if (window.location.pathname === "/") {
      const { default: LandingApp } = await import("./App");

      createRoot(rootElement).render(
        <AppErrorBoundary>
          <LandingApp />
        </AppErrorBoundary>,
      );

      // The landing route does not emit startup mount signals, so clear the boot shell
      // after initial React boot to avoid masking landing content.
      if (!isLikelyTelegramHost()) {
        window.setTimeout(() => {
          hideBootShell();
        }, 1500);
      }
      return;
    }

    const { bootstrapNonLanding } = await import("./bootstrapNonLanding");
    bootstrapNonLanding(rootElement);
  };

  void mountReactTree();
}
